<template>
    <div class="container" data-cy="oneroster-component">
        <div style="max-width: 100%" class="mt-2">
            
            <v-card class="mb-4">
			<v-card-title class="d-flex align-center justify-space-between">
				<span>OneRoster Configuration</span>

				<v-btn
					v-if="!showConfigForm"
					icon
					@click="showConfigForm = true"
					size="small"
					rounded="lg"
					color="grey lighten-1"
					title="Edit Configuration"
				>
					<v-icon size="18">mdi-pencil</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text v-if="showConfigForm">
				<div>
					<v-form ref="configForm">
						<v-row>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.client_id"
									label="Client ID"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.client_id" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.client_secret"
									label="Client Secret"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.client_secret" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.token_url"
									label="Token URL"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.token_url" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.base_url"
									label="Base URL"
									variant="outlined"
									density="comfortable"
									required
									:error="baseUrlError !== null"
									:error-messages="baseUrlError ? [baseUrlError] : []"
									@blur="validateBaseUrlField"
									@input="validateBaseUrlField"
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.base_url" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.url_suffix"
									label="URL Suffix"
									variant="outlined"
									density="comfortable"
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<v-text-field
									v-model="configData.url_suffix"
									label="DBN Code (School Code)"
									variant="outlined"
									density="comfortable"
									v-else
								></v-text-field>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.version"
									label="API Version"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.version" v-else />
							</v-col>
						</v-row>
					</v-form>
				</div>
			</v-card-text>
			<v-card-actions v-if="showConfigForm">
				<v-spacer />
				<v-btn color="grey lighten-1" @click="cancelConfigEdit" :disabled="savingConfig">
					Cancel
				</v-btn>
				<v-btn color="primary" @click="saveConfig" :loading="savingConfig">
					Save Configuration
				</v-btn>
			</v-card-actions>
		    </v-card>
            
        </div>
        <ecsTaskSync integration_type="oneroster" :has-valid-config="hasValidConfig"/>
    </div>
</template>
<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import EcsTaskSync from '@/components/school/integration/ecsTaskSync.vue';
import { useDistrictStore } from '@/stores/district';
import { useSchoolStore } from '@/stores/school';
import FetchService from '@/services/fetch.service';

interface OneRosterSchool {
	school_id: number | null;
	name: string;
	sourced_id: string;
	creating?: boolean;
}

interface OneRosterConfig {
	school_id?: number;
	district_id?: number;
	client_id: string;
	client_secret: string;
	token_url: string;
	base_url: string;
	url_suffix: string | null;
	version: string;
}
const config = ref<OneRosterConfig | null>(null);
const configData = ref<OneRosterConfig>({
	client_id: "",
	client_secret: "",
	token_url: "",
	base_url: "",
	url_suffix: null,
	version: "1.1", // Will be overridden in onMounted based on integration_type
});
const districtStore = useDistrictStore();
const schoolStore = useSchoolStore();
const configForm = ref<any>(null);
const schools = ref<{ school_id: number; school_name: string }[]>([]);



const showConfigForm = ref(false);
const savingConfig = ref(false);
const showSchoolMapping = ref(false);
const baseUrlError = ref<string | null>(null);
const props = defineProps<{
    integration_type?: string | number;
}>();

const emit = defineEmits<{  
    'reload:data': [];
}>();

const fetchOneRosterConfig = async () => {
	try {
		const res = await FetchService.fetchData("/integration/oneroster", "get");
		if (res) {
			config.value = res;
			// Initialize the form data with the current config
			configData.value = { ...res };
			// Override version with prop value only for nycdoe, preserve 1.2 for others
			if (props.integration_type === "nycdoe") {
				configData.value.version = "nycdoe";
			} else {
				// For non-nycdoe integrations, preserve 1.2 if already saved, otherwise default to 1.1
				configData.value.version = configData.value.version === "1.2" ? "1.2" : "1.1";
			}
			// If config is null, show the form by default
			showConfigForm.value = !res;
		} else {
			showConfigForm.value = true;
		}
	} catch (error) {
		console.error("Error fetching OneRoster config:", error);
		showConfigForm.value = true;
	}
};

const cancelConfigEdit = () => {
	if (config.value) {
		configData.value = { ...config.value };
		if (props.integration_type === "nycdoe") {
			configData.value.version = "nycdoe";
		} else {
			configData.value.version = configData.value.version === "1.2" ? "1.2" : "1.1";
		}
	} else {
		configData.value = {
			client_id: "",
			client_secret: "",
			token_url: "",
			base_url: "",
			url_suffix: null,
			version: props.integration_type === "nycdoe" ? "nycdoe" : "1.1",
		};
	}
	showConfigForm.value = false;
	baseUrlError.value = null; // Clear validation error when canceling
};

// Pure validation function that doesn't modify state
const validateBaseUrl = (value: string, version: string): boolean => {
	if (!value) {
		return true; // Let required handle empty
	}
	
	const isInfiniteCampus = schoolStore.school?.integration === 'infinitecampus';
	
	// Must start with https://
	if (!value.startsWith('https://')) {
		return false;
	}
	
	// Version-specific validation
	if (version === '1.1') {
		if (isInfiniteCampus) {
			// Infinite Campus v1.1 pattern: /campus/api/oneroster/v1p1/[district]/ims/oneroster/v1p1 (no trailing slash)
			const infiniteCampusPattern = /^https:\/\/[^\/]+\/campus\/api\/oneroster\/v1p1\/[^\/]+\/ims\/oneroster\/v1p1$/;
			return infiniteCampusPattern.test(value);
		} else {
			// Regular patterns (Follett or Guru-LHRIC)
			// Pattern 1: Guru-LHRIC - /api/ims/oneroster/v1p1/ (with trailing slash, with /api/ prefix)
			const guruLhricPattern = /^https:\/\/[^\/]+(?::\d+)?\/api\/ims\/oneroster\/v1p1\/$/;
			// Pattern 2: Regular (Follett) - /ims/oneroster/v1p1/ (with trailing slash, no /api/ prefix)
			const regularPattern = /^https:\/\/[^\/]+\/ims\/oneroster\/v1p1\/$/;
			// Pattern 3: Alma - /ims/oneroster/v1p1/ (with trailing slash, no /api/ prefix)
			const almaPattern = /^https:\/\/[^\/]+(\/[^\/]+)*\/ims\/oneroster\/v1p1\/$/;
			return guruLhricPattern.test(value) || regularPattern.test(value) || almaPattern.test(value);
			
		}
	}
	
	if (version === '1.2') {
		if (isInfiniteCampus) {
			// Infinite Campus v1.2 pattern
			const infiniteCampusPattern = /^https:\/\/[^\/]+\/campus\/api\/oneroster\/v1p2\/[^\/]+\/ims\/oneroster\/rostering\/v1p2\/$/;
			return infiniteCampusPattern.test(value);
		} else {
			// For non-Infinite Campus v1.2, we may need to add patterns as they're discovered
			// For now, validate that it contains the version pattern
			const hasVersionPattern = /\/ims\/oneroster\/.*v1p2/.test(value);
			return hasVersionPattern && value.startsWith('https://');
		}
	}
	
	// For other versions (like 'nycdoe'), skip validation
	return true;
};

const validateBaseUrlField = () => {
	const value = configData.value.base_url;
	
	if (!value) {
		baseUrlError.value = null; // Let required handle empty
		return true;
	}
	
	const version = configData.value.version;
	const isValid = validateBaseUrl(value, version);
	
	if (!isValid) {
		const isInfiniteCampus = schoolStore.school?.integration === 'infinitecampus';
		if (version === '1.1') {
			if (isInfiniteCampus) {
				baseUrlError.value = 'Base URL must match Infinite Campus OneRoster v1.1 format: /campus/api/oneroster/v1p1/[district]/ims/oneroster/v1p1';
			} else {
				baseUrlError.value = 'Base URL must match OneRoster v1.1 format:\n- /ims/oneroster/v1p1/\n- /api/ims/oneroster/v1p1/';
			}
		} else if (version === '1.2') {
			if (isInfiniteCampus) {
				baseUrlError.value = 'Base URL must match Infinite Campus OneRoster v1.2 format: /campus/api/oneroster/v1p2/[district]/ims/oneroster/rostering/v1p2/';
			} else {
				baseUrlError.value = 'Base URL must contain /ims/oneroster/ with version v1p2';
			}
		} else {
			baseUrlError.value = 'Base URL must start with https://';
		}
		return false;
	}
	
	baseUrlError.value = null;
	return true;
};

// Watch for version changes to re-validate base_url
watch(() => configData.value.version, () => {
	if (configData.value.base_url) {
		validateBaseUrlField();
	}
});

// Watch for integration changes to re-validate base_url
watch(() => schoolStore.school?.integration, () => {
	if (configData.value.base_url) {
		validateBaseUrlField();
	}
});

const hasValidConfig = computed(() => {
	const cfg = config.value;
	if (!cfg) {
		return false;
	}
	
	// For nycdoe integration, only url_suffix (DBN Code) is required
	if (props.integration_type === 'nycdoe') {
		return !!(cfg.url_suffix && cfg.url_suffix.trim());
	}
	
	// For other integrations, validate required fields
	const hasRequiredFields = !!(
		cfg.client_id?.trim() &&
		cfg.client_secret?.trim() &&
		cfg.token_url?.trim() &&
		cfg.base_url?.trim() &&
		cfg.version?.trim()
	);
	
	if (!hasRequiredFields) {
		return false;
	}
	
	// Validate base_url format - must be valid or hasValidConfig is false
	const baseUrlValid = validateBaseUrl(cfg.base_url, cfg.version);
	if (!baseUrlValid) {
		return false;
	}
	
	return true;
});

const saveConfig = async () => {
	// Validate base URL before saving
	const isValid = validateBaseUrlField();
	if (!isValid) {
		// Error message is already set by validateBaseUrlField
		return;
	}
	
	if (configForm.value) {
		const { valid } = await configForm.value.validate();
		if (!valid) {
			return;
		}
	}
	savingConfig.value = true;
	try {
		const payload = {
			client_id: configData.value.client_id,
			client_secret: configData.value.client_secret,
			token_url: configData.value.token_url,
			base_url: configData.value.base_url,
			url_suffix: configData.value.url_suffix,
			version: configData.value.version,
			school_id: schoolStore.school?.school_id,
		};
		const res = await FetchService.fetchData("/oneroster", "post", payload);
		if (res) {
			config.value = res;
			configData.value = { ...res };
			baseUrlError.value = null; // Clear error on successful save
			showConfigForm.value = false;
			emit('reload:data');
		}
	} catch (error) {
		console.error("Error saving OneRoster config:", error);
	} finally {
		savingConfig.value = false;
	}
};

onMounted(async () => {
	await fetchOneRosterConfig();
});
</script>
<style scoped lang="scss"></style>